import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchCompanyUsers,
  deleteCompanyUser,
  setQuery,
  resetForCompany,
} from "../../companyUsers/companyUsersSlice";
import UserFormDialog from "../../companyUsers/UserFormDialog";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../../components/common/DangerConfirmField";

const ROLE_LABELS = {
  1: "Standard",
  5: "Admin",
  6: "Operator",
};

const RoleChip = ({ role }) => {
  const label = ROLE_LABELS[role] || role || "—";
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 18,
        fontSize: "0.6875rem",
        borderRadius: "2px",
        backgroundColor: "#eef3fb",
        color: "#0a4f9e",
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

export default function CompanyUsersTab() {
  const { company } = useOutletContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { items, pagination, query, loading, companyId } = useSelector(
    (s) => s.companyUsers
  );

  const [searchInput, setSearchInput] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  useEffect(() => {
    dispatch(resetForCompany(company._id));
  }, [company._id, dispatch]);

  useEffect(() => {
    if (companyId === company._id) {
      dispatch(fetchCompanyUsers({ companyId: company._id, params: query }));
    }
  }, [dispatch, company._id, companyId, query]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    dispatch(setQuery({ search: searchInput.trim(), page: 1 }));
  };

  const onClearSearch = () => {
    setSearchInput("");
    dispatch(setQuery({ search: "", page: 1 }));
  };

  const onCreate = () => {
    setEditTarget(null);
    setFormOpen(true);
  };

  const onEdit = (u) => {
    setEditTarget(u);
    setFormOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (!isDangerCodeValid(deleteConfirm)) return;
    const res = await dispatch(deleteCompanyUser(deleteTarget._id));
    if (deleteCompanyUser.fulfilled.match(res)) {
      toast.success("User deleted");
      setDeleteTarget(null);
      setDeleteConfirm("");
    } else {
      toast.error(res.payload || "Delete failed");
    }
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirm("");
  };

  return (
    <>
      <Paper variant="outlined">
        <Stack
          direction="row"
          spacing={1}
          sx={{
            p: 1,
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            component="form"
            onSubmit={onSearchSubmit}
            sx={{ display: "flex", flex: { xs: "0 0 90%", md: "1 1 auto" }, minWidth: 0 }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={isMobile ? "Search…" : "Search by name, username, role, phone…"}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={onClearSearch} sx={{ minWidth: 0, p: 0.25 }}>
                      Clear
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isMobile ? (
            <IconButton
              color="primary"
              onClick={onCreate}
              sx={{
                flexShrink: 0,
                backgroundColor: "primary.main",
                color: "common.white",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreate}
              sx={{ flexShrink: 0 }}
            >
              Add User
            </Button>
          )}
        </Stack>

        {isMobile ? (
          <Box sx={{ p: 1, backgroundColor: "background.default" }}>
            {loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant="caption" display="block">
                  No users for this company yet.
                </Typography>
                <MuiLink
                  component="button"
                  onClick={onCreate}
                  sx={{ fontSize: "0.75rem", mt: 0.5 }}
                >
                  Add the first one
                </MuiLink>
              </Box>
            )}
            <Stack spacing={0.75}>
              {items.map((u) => {
                const labelSx = {
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  fontWeight: 600,
                  minWidth: 70,
                  flexShrink: 0,
                };
                const valueSx = {
                  fontSize: "0.875rem",
                  color: "text.primary",
                  fontWeight: 500,
                  wordBreak: "break-word",
                };
                return (
                  <Paper
                    key={u._id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      backgroundColor: "primary.main",
                      borderColor: "primary.dark",
                      transition: "background-color .15s, border-color .15s, box-shadow .15s",
                      "& .MuiTypography-root": { color: "#ffffff" },
                      "& .MuiSvgIcon-root": { color: "#ccfbf1" },
                      "&:hover": {
                        backgroundColor: "primary.dark",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Name:</Typography>
                        <Typography sx={valueSx}>{u.name || "—"}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Username:</Typography>
                        <Typography sx={{ ...valueSx, fontFamily: "monospace" }}>
                          {u.username || "—"}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          pt: 0.5,
                          mt: 0.25,
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/companies/${company._id}/users/${u._id}/devices`
                            )
                          }
                          sx={{ p: 0.5 }}
                        >
                          <RouterIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(u)}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(u)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon
                            fontSize="small"
                            sx={{ color: "error.main" }}
                          />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        ) : (
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, whiteSpace: "nowrap" }}>#</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Name</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Username</TableCell>
                <TableCell sx={{ width: 100, whiteSpace: "nowrap" }}>Role</TableCell>
                <TableCell sx={{ width: 90, whiteSpace: "nowrap" }} align="center">
                  Devices
                </TableCell>
                <TableCell sx={{ width: 130, whiteSpace: "nowrap" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    No users for this company yet.{" "}
                    <MuiLink component="button" onClick={onCreate}>
                      Add the first one
                    </MuiLink>
                  </TableCell>
                </TableRow>
              )}
              {items.map((u, idx) => (
                <TableRow key={u._id} hover>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {(pagination.page - 1) * pagination.limit + idx + 1}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{u.name || "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {u.username}
                  </TableCell>
                  <TableCell>
                    <RoleChip role={u.role} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={u.assignedDeviceCount ?? 0}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6875rem",
                        borderRadius: "2px",
                        backgroundColor: "#f1f1f1",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Assign devices">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/companies/${company._id}/users/${u._id}/devices`
                            )
                          }
                        >
                          <RouterIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(u)}
                        >
                          <DeleteIcon
                            fontSize="small"
                            sx={{ color: "error.main" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      <UserFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        companyId={company._id}
        user={editTarget}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDelete}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } } }}
        fullWidth
      >
        <DialogTitle sx={{ p: 1.75, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "error.main" }}>
            Delete User
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Permanently delete <b>{deleteTarget?.name}</b> (
            {deleteTarget?.username})?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will remove the user, their device-order configurations, and
            unlink them from every device. <b>This cannot be undone.</b>
          </Typography>
          <DangerConfirmField
            value={deleteConfirm}
            onChange={setDeleteConfirm}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={closeDelete} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={onConfirmDelete}
            variant="contained"
            color="error"
            disabled={!isDangerCodeValid(deleteConfirm)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
